import React, { useCallback, useEffect, useRef, useState } from "react";
import Rocket, { isVerticalShip } from "@/components/Rocket";

// Reusable draggable + tappable progress lane. Ship snaps to the nearest stage.
export default function ProgressTrack({ pupil, stages, onSnap, shipSize = 60, tall = false, markerIndex = null }) {
  const trackRef = useRef(null);
  const [dragX, setDragX] = useState(null);
  const dragXRef = useRef(null);
  const draggingRef = useRef(false);

  const n = stages.length;
  const PAD = 6;
  const stagePct = (i) => (n <= 1 ? PAD : PAD + (i / (n - 1)) * (100 - 2 * PAD));

  const pointerMove = useCallback((clientX) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    let x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    dragXRef.current = x;
    setDragX(x);
  }, []);

  const endDrag = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const track = trackRef.current;
    const x = dragXRef.current;
    if (track && x != null) {
      const pct = (x / track.getBoundingClientRect().width) * 100;
      let nearest = 0;
      let best = Infinity;
      for (let i = 0; i < n; i++) {
        const d = Math.abs(stagePct(i) - pct);
        if (d < best) { best = d; nearest = i; }
      }
      onSnap(pupil, nearest);
    }
    dragXRef.current = null;
    setDragX(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, onSnap, pupil]);

  useEffect(() => {
    const move = (e) => {
      if (!draggingRef.current) return;
      if (e.cancelable) e.preventDefault();
      pointerMove(e.touches ? e.touches[0].clientX : e.clientX);
    };
    const up = () => endDrag();
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [pointerMove, endDrag]);

  const startDrag = (e) => {
    draggingRef.current = true;
    pointerMove(e.touches ? e.touches[0].clientX : e.clientX);
  };

  const currentPct = stagePct(pupil.stageIndex);
  const leftPct =
    dragX != null && trackRef.current
      ? (dragX / trackRef.current.getBoundingClientRect().width) * 100
      : currentPct;

  const vertical = isVerticalShip(pupil.variant);

  return (
    <div
      ref={trackRef}
      className={`relative flex-1 ${tall ? "h-24 sm:h-28" : "h-16 sm:h-20"} rounded-2xl bg-white/[0.04] border border-[#252B5C] overflow-visible`}
    >
      <div className="absolute top-1/2 left-3 right-3 -translate-y-1/2 border-t-2 border-dashed border-white/15" />
      {markerIndex != null && (
        <div
          data-testid="teacher-pace-marker"
          className="absolute top-0 bottom-0 flex flex-col items-center justify-center pointer-events-none"
          style={{ left: `calc(${stagePct(markerIndex)}% )`, transform: "translateX(-50%)" }}
          title="Teacher's expected pace"
        >
          <div className="h-full w-0.5 bg-amber-400/50" />
          <span className="absolute -top-1 text-lg">👑</span>
        </div>
      )}
      {stages.map((s, i) => (
        <React.Fragment key={i}>
          <div
            data-testid={`stage-snap-guide-${i}`}
            className="absolute top-0 bottom-0 flex items-center"
            style={{ left: `calc(${stagePct(i)}% )`, transform: "translateX(-50%)" }}
          >
            <div className="w-1 h-8 sm:h-10 rounded-full opacity-40" style={{ backgroundColor: s.color }} />
          </div>
          {/* tap-to-fly hotspot */}
          <button
            type="button"
            data-testid={`stage-tap-${pupil.id}-${i}`}
            onClick={() => onSnap(pupil, i)}
            aria-label={`Move to ${s.name}`}
            className="absolute top-0 bottom-0 w-[14%] -translate-x-1/2"
            style={{ left: `calc(${stagePct(i)}% )` }}
          />
        </React.Fragment>
      ))}
      <button
        data-testid={`pupil-rocket-handle-${pupil.id}`}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        className="absolute top-1/2 z-10 touch-none select-none cursor-grab active:cursor-grabbing"
        style={{
          left: `calc(${leftPct}% )`,
          transform: "translate(-50%, -50%)",
          transition: draggingRef.current ? "none" : "left 0.55s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        <div
          className={`rounded-full p-1 ${vertical ? "rocket-tilt" : ""}`}
          style={{ filter: `drop-shadow(0 0 12px ${pupil.color})` }}
        >
          <Rocket color={pupil.color} variant={pupil.variant} size={shipSize} />
        </div>
      </button>
    </div>
  );
}
